import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const opts = /** @type {AddEventListenerOptions} */ ({
      signal: this.#abortController.signal,
    });

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), opts);

    this.addEventListener('focusout', this.#handleFocusOut, opts);

    /** Close flyouts (Escape) or move into submenu (Arrow Down); Tab alone does not open. */
    this.addEventListener('keydown', this.#handleMenuKeydown, opts);

    /** Keyboard / screen reader triggers: the button next to each link that has a submenu, and "More". */
    this.addEventListener('click', this.#handleToggleClick, opts);

    /** Inside the open "More" panel, focusing an item shows its submenu (same as hovering it). */
    this.addEventListener('focusin', this.#handleOverflowFocusIn, opts);

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * The "More" button that opens the overflow panel
   * @returns {HTMLElement | null}
   */
  get moreToggle() {
    return this.querySelector('.menu-list__list-item--more .menu-list__link');
  }

  /**
   * Whether the overflow panel ("More") is open
   * @returns {boolean}
   */
  get overflowExpanded() {
    return Boolean(this.#state.activeItem) && this.dataset.overflowExpanded === 'true';
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { target?: Element }} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | { target?: Element }} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.moreToggle?.setAttribute('aria-expanded', (!isDefaultSlot).toString());

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.removeAttribute('data-expanded');
      findToggle(previouslyActiveItem)?.setAttribute('aria-expanded', 'false');
    }

    this.#state.activeItem = item;
    // Open state lives in a data attribute (not aria-expanded): the trigger is a link, and
    // aria-expanded on it was flagged as an unnecessary ARIA attribute.
    item.setAttribute('data-expanded', 'true');
    findToggle(item)?.setAttribute('aria-expanded', 'true');

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null | undefined} [item]
   * @param {boolean} [ignoreOverflowHover] Close even when overflow panel is hovered (Escape / keyboard focus moved away).
   */
  #deactivate = (item = this.#state.activeItem, ignoreOverflowHover = false) => {
    const currentItem = item ?? null;
    if (!currentItem || currentItem !== this.#state.activeItem) return;

    /** @note Let pointer users move from the navbar into the flyout via overflow hover detection */
    if (!ignoreOverflowHover && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.moreToggle?.setAttribute('aria-expanded', 'false');

    this.#state.activeItem = null;
    currentItem.setAttribute('data-animating', '');
    findToggle(currentItem)?.setAttribute('aria-expanded', 'false');

    setTimeout(() => {
      currentItem.removeAttribute('data-animating');
      // Drop the open state once the closing animation is done, so the hidden submenu's links
      // leave the tab order (unless the item was reopened in the meantime).
      if (this.#state.activeItem !== currentItem) currentItem.removeAttribute('data-expanded');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /** @returns {void} */
  #debouncedDeactivate = debounce(() => this.#deactivate(undefined, false), DEACTIVATE_DELAY);

  /**
   * Whether a node is inside the open branch: the active item's row (link + toggle + submenu) or,
   * while the "More" panel is open, the "More" button and any item in the panel.
   * @param {Node | null} node
   * @returns {boolean}
   */
  #isInOpenBranch(node) {
    const anchor = this.#state.activeItem;
    if (!anchor || !(node instanceof Node)) return false;
    if (anchor.closest('.menu-list__list-item')?.contains(node)) return true;

    const element = node instanceof Element ? node : node.parentElement;
    return this.overflowExpanded && Boolean(element?.closest('.menu-list__list-item:is([slot="overflow"], [slot="more"])'));
  }

  /**
   * When keyboard focus moves out of an open mega-menu branch, force close unless focus is still within it.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const anchor = this.#state.activeItem;
    if (!(anchor instanceof HTMLElement)) return;

    /** @note During focusout, document.activeElement can still be the old node; relatedTarget is authoritative */
    if (this.#isInOpenBranch(/** @type {Node | null} */ (event.relatedTarget))) return;

    const closeIfFocusLeft = () => {
      if (this.#state.activeItem !== anchor) return;
      if (this.#isInOpenBranch(document.activeElement)) return;

      this.#close();
    };

    if (event.relatedTarget == null) {
      requestAnimationFrame(() => closeIfFocusLeft());
      return;
    }

    queueMicrotask(closeIfFocusLeft);
  };

  /**
   * Inside the open "More" panel, show the submenu of the item whose link receives focus.
   * @param {FocusEvent} event
   */
  #handleOverflowFocusIn = (event) => {
    if (!this.overflowExpanded || !(event.target instanceof Element)) return;

    const listItem = event.target.closest('[ref="menuitem"]')?.closest('.menu-list__list-item[slot="overflow"]');
    if (listItem instanceof HTMLElement) this.activate({ target: listItem });
  };

  /**
   * Toggle a submenu from its adjacent button (Enter / Space / click).
   * @param {MouseEvent} event
   */
  #handleToggleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const more = event.target.closest('.menu-list__list-item--more');
    if (more instanceof HTMLElement && event.target.closest('.menu-list__link')) {
      if (!this.overflowExpanded) {
        this.activate({ target: more });
      } else if (event.detail === 0) {
        // Keyboard activation (Enter/Space) closes; a pointer click right after the hover that opened
        // the panel should not.
        this.#close();
      }
      return;
    }

    const toggle = event.target.closest('.menu-list__submenu-toggle');
    const listItem = toggle?.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;

    if (findMenuItem(listItem) === this.#state.activeItem) {
      this.#close();
    } else {
      this.activate({ target: listItem });
    }
  };

  /**
   * Close the open submenu right away (no hover grace period).
   */
  #close() {
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(undefined, true);
  }

  /**
   * Keyboard support for submenu flyouts — never open mega menus on mere Tab/focus alone.
   * @param {KeyboardEvent} event
   */
  #handleMenuKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.key === 'Escape') {
      const activeItem = this.#state.activeItem;
      if (!activeItem || !this.#isInOpenBranch(event.target)) return;

      // Return focus to the control that opened it: "More", the item's toggle, or the link itself.
      const opener = (this.overflowExpanded && this.moreToggle) || findToggle(activeItem) || activeItem;

      event.preventDefault();
      this.#close();
      opener.focus();
      return;
    }

    if (event.key !== 'ArrowDown') return;

    const more = event.target.closest('.menu-list__list-item--more');
    if (more instanceof HTMLElement) {
      event.preventDefault();
      if (!this.overflowExpanded) this.activate({ target: more });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const firstOverflowLink = this.querySelector('.menu-list__list-item[slot="overflow"] [ref="menuitem"]');
          if (firstOverflowLink instanceof HTMLElement) firstOverflowLink.focus();
        });
      });
      return;
    }

    const trigger = event.target.closest('[ref="menuitem"], .menu-list__submenu-toggle');
    const listItem = trigger?.closest('.menu-list__list-item');
    const menuitem = listItem instanceof HTMLElement ? findMenuItem(listItem) : null;
    if (!menuitem?.hasAttribute('data-has-submenu') || !findSubmenu(menuitem)) return;

    event.preventDefault();
    if (menuitem !== this.#state.activeItem) this.activate({ target: listItem });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#focusFirstSubmenuFocusable(menuitem));
    });
  };

  /**
   * @param {HTMLElement} anchorMenuitem
   */
  #focusFirstSubmenuFocusable(anchorMenuitem) {
    const submenu = findSubmenu(anchorMenuitem);
    if (!(submenu instanceof HTMLElement)) return;
    const candidate = submenu.querySelector(
      '.menu-list__submenu-inner a[href]:not([tabindex="-1"]), .menu-list__submenu-inner button:not([disabled]):not([aria-hidden="true"])'
    );
    if (candidate instanceof HTMLElement) candidate.focus();
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the submenu toggle button next to a menu item link.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findToggle(element) {
  const toggle = element?.parentElement?.querySelector(':scope > .menu-list__submenu-toggle');
  return toggle instanceof HTMLElement ? toggle : null;
}
